import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { useActiveGroup } from '../hooks/useActiveGroup';
import { useAuth } from '../hooks/useAuth';
import { 
  Plus, 
  Calendar, 
  MapPin, 
  Clock, 
  Users, 
  Link as LinkIcon, 
  Copy, 
  Check, 
  Trash2, 
  Pencil,
  ExternalLink,
  AlertCircle,
  Trophy,
  Loader2,
  Shuffle,
  FileText
} from 'lucide-react';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CreateGameModal from '../components/games/CreateGameModal';
import EditGameModal from '../components/games/EditGameModal';
import { useToast } from '../contexts/ToastContext';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import PlanLimitModal from '../components/PlanLimitModal';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

interface Game {
  id: string;
  titulo: string;
  title: string; // This is a derived property for display
  datetime: string;
  location: string | null;
  public_token: string;
  created_at: string;
  times: any[] | null;
  confirmations: {
    total: number;
    confirmed: number;
    declined: number;
  };
}

const GamesManagementPage: React.FC = () => {
  const { supabase } = useSupabase();
  const { activeGroup } = useActiveGroup();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { limits } = usePlanFeatures();
  
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [filterMonth, setFilterMonth] = useState<'all' | string>('all');

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);

const fetchGames = async () => {
  if (!activeGroup) return;

  setLoading(true);
  setError(null);

  try {
    let query = supabase
      .from('jogos')
      .select('id, titulo, data, local, public_token, criado_em, times')
      .eq('grupo_id', activeGroup.id);

    // Se filterMonth estiver definido e não for 'all', aplica filtro por mês
    if (filterMonth && filterMonth !== 'all') {
      const year = Number(filterMonth.slice(0, 4));
      const month = Number(filterMonth.slice(5, 7)) - 1;

      const start = new Date(year, month, 1);
      start.setHours(0, 0, 0, 0);
      const startDate = start.toISOString();

      const end = new Date(year, month + 1, 0);
      end.setHours(23, 59, 59, 999);
      const endDate = end.toISOString();

      query = query.gte('data', startDate).lte('data', endDate);
    }

    // Ordenar por data (decrescente)
    const { data: gamesData, error: gamesError } = await query.order('data', { ascending: false });

    if (gamesError) throw gamesError;

    // Processar confirmações como você já tinha
    const processedGames = await Promise.all((gamesData || []).map(async (game) => {
      const { data: confirmationsData, error: confirmationsError } = await supabase
        .from('game_confirmations')
        .select('status')
        .eq('game_id', game.id);

      const confirmations = confirmationsData || [];
      const confirmed = confirmations.filter(c => c.status === 'confirmed').length;
      const declined = confirmations.filter(c => c.status === 'declined').length;

      return {
        ...game,
        datetime: game.data,
        confirmations: {
          total: confirmations.length,
          confirmed,
          declined,
        },
      };
    }));

    setGames(processedGames);
  } catch (err: any) {
    console.error('Erro ao buscar jogos:', err);
    setError(err.message || 'Erro ao carregar jogos');
  } finally {
    setLoading(false);
  }
};




  useEffect(() => {
    fetchGames();
  }, [activeGroup, filterMonth, supabase]);

  const copyGameLink = async (token: string) => {
    const link = `${window.location.origin}/confirmar/${token}`;
    
    try {
      await navigator.clipboard.writeText(link);
      showSuccess('Link copiado!', 'O link foi copiado para a área de transferência');
    } catch (err) {
      console.error('Erro ao copiar link:', err);
      showError('Erro ao copiar', 'Não foi possível copiar o link');
    }
  };

  const shareGameOnWhatsApp = async (token: string) => {
    const link = `${window.location.origin}/confirmar/${token}`;
    
    // Encontrar o jogo correspondente para obter informações
    const game = games.find(g => g.public_token === token);
    if (!game) return;
    
    const gameDate = game.datetime ? format(parseGameDateTime(game.datetime), 'PPP', { locale: ptBR }) : 'Data inválida';
    const gameTime = game.datetime ? format(parseGameDateTime(game.datetime), 'HH:mm') : 'Horário inválido';
    
    let message = `⚽ *${game.titulo}*\n\n`;
    message += `📅 *Data:* ${gameDate}\n`;
    message += `🕐 *Horário:* ${gameTime}\n`;
    
    if (game.local) {
      message += `📍 *Local:* ${game.local}\n`;
    }
    
    message += `\n👥 *Confirmações até agora:*\n`;
    message += `✅ ${game.confirmations.confirmed} confirmados\n`;
    message += `❌ ${game.confirmations.declined} não vão\n`;
    
    message += `\n🔗 *Confirme sua presença:*\n${link}`;
    message += `\n\n_Enviado via Presida.Club_`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const handleDelete = async (gameId: string) => {
    if (!confirm('Tem certeza que deseja excluir este jogo? Esta ação não pode ser desfeita.')) {
      return;
    }

    setDeleting(gameId);
    console.log('Starting deletion process for game:', gameId);
    console.log('Current games in state before deletion:', games.length, games.map(g => ({ id: g.id, titulo: g.titulo })));
    
    try {
      // First delete related confirmations
      const { error: confirmationsError } = await supabase
        .from('game_confirmations')
        .delete()
        .eq('game_id', gameId);

      if (confirmationsError) {
        console.error('Error deleting confirmations for game', gameId, ':', confirmationsError);
        // Continue with game deletion even if confirmations fail
      } else {
        console.log('Successfully deleted confirmations for game:', gameId);
      }

      // Delete game participations
      const { error: participationsError } = await supabase
        .from('jogos_jogadores')
        .delete()
        .eq('jogo_id', gameId);

      if (participationsError) {
        console.error('Error deleting participations for game', gameId, ':', participationsError);
        // Continue with game deletion even if participations fail
      } else {
        console.log('Successfully deleted participations for game:', gameId);
      }

      // Finally delete the game
      const { error } = await supabase
        .from('jogos')
        .delete()
        .eq('id', gameId);

      if (error) {
        console.error('Error deleting game from jogos table:', error);
        throw error;
      }

      console.log('Game deleted successfully from database:', gameId);
      
      // Update local state immediately
      console.log('Updating local state - removing game:', gameId);
      setGames(prev => prev.filter(game => game.id !== gameId));
      console.log('Local state updated');
      showSuccess('Jogo excluído', 'O jogo foi excluído com sucesso');
      
      // Force refresh immediately to ensure data consistency
      console.log('Forcing data refresh...');
      await fetchGames();
      console.log('Data refresh completed');
    } catch (err: any) {
      console.error('Erro ao excluir jogo:', err);
      showError('Erro ao excluir', err.message || 'Erro ao excluir jogo');
      // If deletion failed, refresh to ensure UI is in sync with database
      await fetchGames();
    } finally {
      setDeleting(null);
    }
  };

  const openConfirmationPage = (token: string) => {
    window.open(`/confirmar/${token}`, '_blank');
  };

  const generateGamePDF = async (game: Game) => {
  setGeneratingPdf(game.id);

  try {
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('nome, telefone')
      .eq('id', user?.id)
      .single();

    if (profileError) {
      throw new Error('Erro ao buscar dados do usuário');
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Helper: escreve texto centralizado
    const centerText = (text: string, size = 12, gap = 10) => {
      doc.setFontSize(size);
      doc.text(text, pageWidth / 2, yPos, { align: 'center' });
      yPos += gap;
    };

    // 1. Convocação
    centerText('CONVOCAÇÃO PARA O JOGO', 20, 20);

    // 2. Título
    centerText(game.titulo, 16, 20);

    // 3. Data
    const gameDate = game.data
      ? format(parseGameDateTime(game.data), 'PPP', { locale: ptBR })
      : 'Data inválida';
    centerText(`Data: ${gameDate}`, 12);

    // 4. Horário
    const gameTime = game.data
      ? format(parseGameDateTime(game.data), 'HH:mm')
      : 'Horário inválido';
    centerText(`Horário: ${gameTime}`, 12);

    // 5. Local
    if (game.local) {
      centerText(`Local: ${game.local}`, 12);
    }
    yPos += 20;

    // 6. QR Code
    const confirmationLink = `${window.location.origin}/confirmar/${game.public_token}`;
    const qrCodeDataUrl = await QRCode.toDataURL(confirmationLink, {
      width: 200,
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' }
    });

    const qrSize = 60;
    const qrX = (pageWidth - qrSize) / 2;
    doc.addImage(qrCodeDataUrl, 'PNG', qrX, yPos, qrSize, qrSize);
    yPos += qrSize + 20;

    // 7. Contato
    centerText('Contato:', 14, 10);

    if (profileData?.nome) {
      centerText(`Nome: ${profileData.nome}`, 12);
    }
    if (profileData?.telefone) {
      centerText(`Telefone: ${profileData.telefone}`, 12);
    }

    const fileName = `convocacao-${game.titulo.replace(/[^a-zA-Z0-9]/g, '-')}-${game.data
      ? format(parseGameDateTime(game.data), 'yyyy-MM-dd')
      : 'sem-data'}.pdf`;

    doc.save(fileName);
    showSuccess('PDF gerado!', 'O arquivo foi baixado com sucesso');
  } catch (error: any) {
    showError('Erro ao gerar PDF', error.message || 'Erro inesperado ao gerar PDF');
  } finally {
    setGeneratingPdf(null);
  }
};


  const handleEditGame = (game: Game) => {
    setSelectedGame(game);
    setShowEditModal(true);
  };

  const handleGameUpdated = (updatedGame: Game) => {
    setGames(prev => prev.map(game => 
      game.id === updatedGame.id ? updatedGame : game
    ));
    setShowEditModal(false);
    setSelectedGame(null);
  };

  if (!activeGroup) {
    return (
      <div className="text-center py-12">
        <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Nenhum grupo selecionado</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Selecione um grupo ativo para gerenciar jogos
        </p>
      </div>
    );
  }
  // Função para fazer parsing correto da data/hora
  const parseGameDateTime = (dateTimeString: string) => {
    try {
      // Primeiro tentar como ISO string
      const isoDate = new Date(dateTimeString);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }
      
      // Se falhar, tentar parse manual
      if (dateTimeString.includes(' ')) {
        return parse(dateTimeString, 'yyyy-MM-dd HH:mm:ss', new Date());
      }
      
      // Fallback para apenas data
      return parse(dateTimeString, 'yyyy-MM-dd', new Date());
    } catch (error) {
      console.error('Erro ao fazer parse da data:', error);
      return new Date(dateTimeString); // Fallback
    }
  };

  return (
    <div>
      <div className="flex flex-col mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Gerenciar Jogos</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Crie jogos e gerencie confirmações de presença
          </p>
          <div className="block sm:hidden">
            <button
              onClick={() => {
                if (!limits.canCreateGame) {
                  setShowPlanModal(true);
                  return;
                }
                setShowCreateModal(true);
              }}
              className="btn-primary w-full"
              disabled={!limits.canCreateGame}
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Jogo
            </button>
          </div>
        </div>
        <div className="hidden sm:flex sm:justify-between sm:items-center">
          <div></div>
          <button
            onClick={() => {
              if (!limits.canCreateGame) {
                setShowPlanModal(true);
                return;
              }
              setShowCreateModal(true);
            }}
            className="btn-primary"
            disabled={!limits.canCreateGame}
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Jogo
          </button>
        </div>
      </div>
 
      {/* Filtros */}
      <div className="card p-4 mb-6">
        <div className="flex items-center gap-4">
          <div>
            <label htmlFor="filterMonth" className="block text-sm font-medium mb-1">
              Mês
            </label>
            <input
              type="month"
              id="filterMonth"
              className="input py-1.5"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
            />
          </div>
          <div className="mt-auto">
            <button 
              onClick={fetchGames}
              className="btn-outline py-1.5"
              title="Atualizar"
            >
              <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p>Carregando jogos...</p>
        </div>
      ) : games.length === 0 ? (
        <div className="card p-8 text-center">
          <Trophy className="w-16 h-16 text-primary-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Nenhum jogo encontrado</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Crie seu primeiro jogo para começar a gerenciar confirmações de presença
          </p>
          <button
            onClick={() => {
              if (!limits.canCreateGame) {
                setShowPlanModal(true);
                return;
              }
              setShowCreateModal(true);
            }}
            className="btn-primary"
          >
            Criar primeiro jogo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {games.map((game) => (
            <div
              key={game.id}
              className="card hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold truncate">{game.titulo}</h3>
                  <div className="flex gap-2"> 
                    <button
                      onClick={() => shareGameOnWhatsApp(game.public_token)}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      title="Compartilhar no WhatsApp"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleEditGame(game)}
                      className="p-2 text-blue-500 hover:text-blue-700 transition-colors"
                      title="Editar jogo"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => generateGamePDF(game)}
                      className="p-2 text-purple-500 hover:text-purple-700 transition-colors"
                      title="Gerar PDF"
                      disabled={generatingPdf === game.id}
                    >
                      {generatingPdf === game.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => openConfirmationPage(game.public_token)}
                      className="p-2 text-blue-500 hover:text-blue-700 transition-colors"
                      title="Abrir página de confirmação"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(game.id)}
                      className="p-2 text-red-500 hover:text-red-700 transition-colors"
                      title="Excluir jogo"
                      disabled={deleting === game.id}
                    >
                      {deleting === game.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {game.data ? format(parseGameDateTime(game.data), 'PPP', { locale: ptBR }) : 'Data inválida'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {game.data ? format(parseGameDateTime(game.data), 'HH:mm') : 'Horário inválido'}
                    </span>
                  </div>

                  {game.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {game.location}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t dark:border-dark-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Confirmações
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-green-600 dark:text-green-400">
                          {game.confirmations.confirmed}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-red-600 dark:text-red-400">
                          {game.confirmations.declined}
                        </span>
                      </div>
                      <span className="text-gray-500">
                        Total: {game.confirmations.total}
                      </span>
                    </div>
                  </div>

                  {/* Times Status */}
                  {game.times && Array.isArray(game.times) && game.times.length > 0 && (
                    <div className="mt-3 pt-3 border-t dark:border-dark-border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-primary-500" />
                          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                            Times Definidos
                          </span>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400">
                          {game.times.length} times
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  {game.times && Array.isArray(game.times) && game.times.length > 0 ? (
                    <button
                      onClick={() => navigate(`/sorteio?game=${game.id}`)}
                      className="btn-outline flex-1 text-sm"
                    >
                      <Shuffle className="w-4 h-4 mr-2" />
                      Sortear Novamente
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(`/sorteio?game=${game.id}`)}
                      className="btn-primary flex-1 text-sm"
                    >
                      <Trophy className="w-4 h-4 mr-2" />
                      Sortear Times
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Criação */}
      <CreateGameModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onGameCreated={() => {
          setShowCreateModal(false);
          fetchGames();
        }}
      />

      {/* Modal de Edição de Jogo */}
      <EditGameModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedGame(null);
        }}
        game={selectedGame}
        onGameUpdated={handleGameUpdated}
      />

      {/* Plan Limit Modal */}
      <PlanLimitModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        message="A criação de jogos não está disponível no seu plano atual. Faça upgrade para acessar este recurso."
      />
    </div>
  );
};

export default GamesManagementPage;   